import React, { ReactElement, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@ui/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@ui/components/ui/dialog';

import { useDeleteChapter, useUpdateChapter } from '@data/hooks/organization';
import type { Chapter } from '@data/utils/types';

interface DeleteChapterDialogProps {
  organizationId: string;
  chapter: Chapter | null;
  open: boolean;
  onClose: () => void;
}

const DeleteChapterDialog = ({ organizationId, chapter, open, onClose }: DeleteChapterDialogProps): ReactElement => {
  const { t } = useTranslation();

  const deleteChapter = useDeleteChapter(organizationId);
  const updateChapter = useUpdateChapter(organizationId);

  // When DELETE returns 409 the chapter is still referenced — we flip the UI to suggest disabling it.
  const [inUse, setInUse] = useState(false);

  useEffect(() => {
    if (open) setInUse(false);
  }, [open, chapter?.id]);

  if (!chapter) {
    return (
      <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
        <DialogContent />
      </Dialog>
    );
  }

  const onConfirmDelete = () => {
    deleteChapter.mutate(chapter.id, {
      onSuccess: () => onClose(),
      onError: error => {
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 409) {
          setInUse(true);
        }
      },
    });
  };

  const onDisableInstead = () => {
    updateChapter.mutate({ chapterId: chapter.id, values: { isActive: false } }, { onSuccess: () => onClose() });
  };

  const isPending = deleteChapter.isPending || updateChapter.isPending;

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{inUse ? t('chaptersAdmin.delete.inUseTitle') : t('chaptersAdmin.delete.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3 py-2">
          <p className="text-sm text-foreground">
            <Trans
              i18nKey={inUse ? 'chaptersAdmin.delete.inUseBody' : 'chaptersAdmin.delete.confirm'}
              values={{ label: chapter.label }}
              components={{ bold: <strong /> }}
            />
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>
            {t('chaptersAdmin.form.cancel')}
          </Button>
          {inUse ? (
            <Button type="button" onClick={onDisableInstead} disabled={isPending}>
              {t('chaptersAdmin.delete.disableInstead')}
            </Button>
          ) : (
            <Button type="button" variant="destructive" onClick={onConfirmDelete} disabled={isPending}>
              {t('chaptersAdmin.actions.delete')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteChapterDialog;
